import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {AdminInputDropdown, AdminInputRow, AdminInputText} from "../../../layout/admin";
import {OrganizationalUnit} from '../types/OrganizationalUnit';
import {formatAddress, formatCoordinates} from '../utils/addressFormatter';
import AddressLocator from './AddressLocator';
import {Button} from 'primereact/button';
import {InputText} from 'primereact/inputtext';

interface AddressFormData {
  street: string;
  streetNumber: string;
  apartmentNumber: string;
  town: string;
  postalCode: string;
  administrationUnitId: string;
  latitude?: number;
  longitude?: number;
}

interface AddressFormSectionProps {
  formData: AddressFormData;
  administrationUnits: OrganizationalUnit[];
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onCoordinatesChange: (latitude?: number, longitude?: number) => void;
  errors?: {
    street?: string;
    streetNumber?: string;
    apartmentNumber?: string;
    town?: string;
    postalCode?: string;
    administrationUnitId?: string;
  };
}

const AddressFormSection: React.FC<AddressFormSectionProps> = ({
  formData,
  administrationUnits,
  onChange,
                                                                 onCoordinatesChange,
                                                                 errors = {}
}) => {
  const {t} = useTranslation();
  const [locatorVisible, setLocatorVisible] = useState(false);

  const administrationUnitOptions = useMemo(
    () => administrationUnits.map(unit => ({
      label: `${unit.name} (${unit.type})`,
      value: unit.id
    })),
    [administrationUnits]
  );

  const formattedAddress = useMemo(
    () => formatAddress({
      street: formData.street,
      streetNumber: formData.streetNumber,
      apartmentNumber: formData.apartmentNumber,
      postalCode: formData.postalCode,
      town: formData.town
    }),
    [formData.street, formData.streetNumber, formData.apartmentNumber, formData.postalCode, formData.town]
  );

  const handleOpenLocator = () => {
    if (!formattedAddress) {
      console.error('Please fill in at least some address fields before selecting coordinates.');
      return;
    }
    setLocatorVisible(true);
  };

  const handleAcceptCoordinates = (latitude: number, longitude: number) => {
    onCoordinatesChange(latitude, longitude);
  };

  const handleClearCoordinates = () => {
    onCoordinatesChange(undefined, undefined);
  };

  return (
    <>
      <AdminInputText
        label={t('admin.address.fields.street')}
        value={formData.street}
        onChange={onChange}
        name="street"
        id="street"
        placeholder={t('admin.address.placeholders.street')}
        error={errors.street}
      />
      <AdminInputRow>
        <AdminInputText
          label={t('admin.address.fields.streetNumber')}
          value={formData.streetNumber}
          onChange={onChange}
          name="streetNumber"
          id="streetNumber"
          placeholder={t('admin.address.placeholders.streetNumber')}
          error={errors.streetNumber}
        />
        <AdminInputText
          label={t('admin.address.fields.apartmentNumber')}
          value={formData.apartmentNumber}
          onChange={onChange}
          name="apartmentNumber"
          id="apartmentNumber"
          placeholder={t('admin.address.placeholders.apartmentNumber')}
          error={errors.apartmentNumber}
        />
      </AdminInputRow>
      <AdminInputRow>
        <AdminInputText
          label={t('admin.address.fields.town')}
          value={formData.town}
          onChange={onChange}
          name="town"
          id="town"
          placeholder={t('admin.address.placeholders.town')}
          error={errors.town}
        />
        <AdminInputText
          label={t('admin.address.fields.postalCode')}
          value={formData.postalCode}
          onChange={onChange}
          name="postalCode"
          id="postalCode"
          placeholder={t('admin.address.placeholders.postalCode')}
          error={errors.postalCode}
        />
      </AdminInputRow>
      <AdminInputRow>
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('admin.address.fields.coordinates')}
          </label>
          <div className="p-inputgroup flex-1 w-full px-4 py-3 border border-gray-300 rounded-sm bg-gray-50">
            <InputText
              value={formatCoordinates(formData.latitude, formData.longitude)}
              disabled
              placeholder={t('admin.address.placeholders.noCoordinates')}
              className="bg-gray-50"
            />
            <Button
              type="button"
              icon="fa fa-location-dot"
              onClick={handleOpenLocator}
              tooltip={t('admin.address.tooltips.selectLocation')}
              tooltipOptions={{position: 'top'}}
              className="w-5"
            />
            {(formData.latitude != null && formData.longitude != null) && (
              <Button
                type="button"
                icon="pi pi-times"
                onClick={handleClearCoordinates}
                tooltip={t('admin.address.tooltips.clearCoordinates')}
                tooltipOptions={{position: 'top'}}
                className="w-5"
              />
            )}
          </div>
        </div>
        <AdminInputDropdown
          label={t('admin.address.fields.administrationUnit')}
          value={formData.administrationUnitId}
          onChange={onChange}
          options={administrationUnitOptions}
          placeholder={t('admin.address.placeholders.selectAdminUnit')}
          name="administrationUnitId"
          id="administrationUnitId"
          error={errors.administrationUnitId}
        />
      </AdminInputRow>

      <AddressLocator
        visible={locatorVisible}
        onHide={() => setLocatorVisible(false)}
        address={formattedAddress}
        initialLatitude={formData.latitude}
        initialLongitude={formData.longitude}
        onAccept={handleAcceptCoordinates}
      />
    </>
  );
};

export default AddressFormSection;

